import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, BookOpen, Calculator, CheckCircle } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './WorkedExampleDisplay.css';
import MathDiagram from './diagrams/MathDiagram';
import ReportContentDialog from './educational/ReportContentDialog';

const WorkedExampleDisplay = ({ example }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const handleReportClose = (wasSuccessful) => {
    setReportOpen(false);
    if (wasSuccessful) setHasReported(true);
  };

  if (!example) {
    return (
      <div className="worked-example-placeholder">
        <BookOpen className="placeholder-icon" />
        <p>No worked example available</p>
      </div>
    );
  }

  const {
    title = 'Worked Example',
    description = '',
    problem = {},
    solution = {},
    finalAnswer = '',
    topic = '',
    subtopic = '',
    grade = '',
    difficulty = 'medium',
    keyConcepts = [],
    commonMistakes = [],
    practiceProblems = []
  } = example;

  const steps = example.steps || solution.steps || [];
  const actualFinalAnswer = finalAnswer || solution.finalAnswer || '';

  // Check if final answer should be displayed
  const shouldShowFinalAnswer = () => {
    if (!actualFinalAnswer) return false;
    const invalidValues = [
      'answer pending',
      'pending',
      'n/a',
      'na',
      'none',
      'null',
      'undefined',
      ''
    ];
    const normalizedAnswer = String(actualFinalAnswer).toLowerCase().trim();
    return !invalidValues.includes(normalizedAnswer);
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  const toggleShowAllSteps = () => {
    setShowAllSteps(!showAllSteps);
  };

  // Convert numeric difficulty to string and get color class
  const getDifficultyColor = (level) => {
    // Handle numeric difficulty (1-5 scale)
    if (typeof level === 'number') {
      if (level <= 2) return 'difficulty-easy';
      if (level <= 3) return 'difficulty-medium';
      return 'difficulty-hard';
    }
    
    // Handle string difficulty
    const levelStr = String(level || 'medium').toLowerCase();
    switch (levelStr) {
      case 'easy':
      case '1':
      case '2':
        return 'difficulty-easy';
      case 'medium':
      case '3':
        return 'difficulty-medium';
      case 'hard':
      case '4':
      case '5':
        return 'difficulty-hard';
      default:
        return 'difficulty-medium';
    }
  };

  // Convert numeric difficulty to display text
  const getDifficultyText = (level) => {
    if (typeof level === 'number') {
      if (level <= 2) return 'Easy';
      if (level <= 3) return 'Medium';
      return 'Hard';
    }
    
    const levelStr = String(level || 'medium').toLowerCase();
    switch (levelStr) {
      case 'easy':
      case '1':
      case '2':
        return 'Easy';
      case 'medium':
      case '3':
        return 'Medium';
      case 'hard':
      case '4':
      case '5':
        return 'Hard';
      default:
        return 'Medium';
    }
  };

  // Render LaTeX content using KaTeX
  // Render LaTeX content using KaTeX - handles multi-line content
  const renderMathContent = (content) => {
    if (!content) return null;

    if (typeof content === 'string') {
      // Split by newlines to handle multi-line content
      const lines = content.split('\n');
      
      // If there's only one line, process it directly
      if (lines.length === 1) {
        return renderSingleLine(content);
      }
      
      // For multi-line content, render each line separately
      return (
        <div className="math-content-block">
          {lines.map((line, lineIndex) => {
            // Skip empty lines but preserve spacing
            if (line.trim() === '') {
              return <div key={lineIndex} style={{ height: '8px' }} />;
            }
            return (
              <div key={lineIndex} style={{ marginBottom: '4px' }}>
                {renderSingleLine(line)}
              </div>
            );
          })}
        </div>
      );
    }

    return <span>{String(content)}</span>;
  };

  // Helper function to render a single line of content with LaTeX
  const renderSingleLine = (text) => {
    if (!text) return null;
    
    // Check if entire line is a LaTeX command (starts with \ and has no $)
    const trimmed = text.trim();
    const isFullLatex = (trimmed.startsWith('\\') && !trimmed.includes('$')) ||
                        (trimmed.includes('\\frac') && !trimmed.includes('$')) ||
                        (trimmed.includes('\\sqrt') && !trimmed.includes('$'));
    
    if (isFullLatex) {
      try {
        const html = katex.renderToString(trimmed, {
          throwOnError: false,
          displayMode: true
        });
        return <span dangerouslySetInnerHTML={{ __html: html }} />;
      } catch (e) {
        return <span>{text}</span>;
      }
    }

    // Process inline LaTeX (content between $ signs)
    const parts = text.split(/(\$\$[^$]+\$\$|\$[^$]+\$)/g);
    
    return (
      <span>
        {parts.map((part, index) => {
          // Display mode LaTeX ($$...$$)
          if (part.startsWith('$$') && part.endsWith('$$')) {
            const latex = part.slice(2, -2);
            try {
              const html = katex.renderToString(latex, {
                throwOnError: false,
                displayMode: true
              });
              return <span key={index} dangerouslySetInnerHTML={{ __html: html }} />;
            } catch (e) {
              return <span key={index}>{latex}</span>;
            }
          }
          // Inline LaTeX ($...$)
          if (part.startsWith('$') && part.endsWith('$')) {
            const latex = part.slice(1, -1);
            try {
              const html = katex.renderToString(latex, {
                throwOnError: false,
                displayMode: false
              });
              return <span key={index} dangerouslySetInnerHTML={{ __html: html }} />;
            } catch (e) {
              return <span key={index}>{latex}</span>;
            }
          }
          // Regular text
          return <span key={index}>{part}</span>;
        })}
      </span>
    );
  };

  const renderStep = (step, index) => {
    const stepContent = step.content || step.explanation || step.text || '';
    const stepTitle = step.title || `Step ${index + 1}`;
    const stepExplanation = step.explanation && step.explanation !== stepContent ? step.explanation : '';

    return (
      <div key={index} className={`step-card ${currentStep === index ? 'active' : ''}`}>
        <div className="step-header">
          <span className="step-number">{index + 1}</span>
          <h4 className="step-title">{stepTitle}</h4>
        </div>
        <div className="step-content">
          {renderMathContent(stepContent)}
        </div>
	{step.image && (
          <div style={{ margin: '8px 0' }}>
            <MathDiagram data={step.image} />
          </div>
        )}
        {stepExplanation && (
          <div className="step-explanation">
            <em>{renderMathContent(stepExplanation)}</em>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="worked-example-display">
      <div className="example-header">
        <div className="example-title-row">
          <BookOpen className="example-icon" />
          <h3 className="example-title">{title}</h3>
        </div>
        <div className="example-meta">
          {topic && <span className="meta-tag topic-tag">{topic}</span>}
          {subtopic && <span className="meta-tag subtopic-tag">{subtopic}</span>}
          {grade && <span className="meta-tag grade-tag">Grade {grade}</span>}
          <span className={`meta-tag ${getDifficultyColor(difficulty)}`}>
            {getDifficultyText(difficulty)}
          </span>
          <span
            className="meta-tag"
            title={hasReported ? "Reported" : "Report an issue"}
            onClick={(e) => { e.stopPropagation(); if (!hasReported) setReportOpen(true); }}
            style={{
              cursor: hasReported ? 'default' : 'pointer',
              color: hasReported ? '#4caf50' : '#bdbdbd',
              fontSize: '14px',
              marginLeft: '4px',
              transition: 'color 0.2s',
              userSelect: 'none'
            }}
            onMouseEnter={(e) => { if (!hasReported) e.currentTarget.style.color = '#f44336'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = hasReported ? '#4caf50' : '#bdbdbd'; }}
          >
            {hasReported ? '\u2713 Reported' : '\u2691 Report'}
          </span>
        </div>
      </div>

      {description && (
        <div className="example-description">
          {renderMathContent(description)}
        </div>
      )}

      {(problem.text || problem.latex) && (
        <div className="problem-section">
          <h4>Problem</h4>
          <div className="problem-content">
            {renderMathContent(problem.text || problem.latex)}
          </div>
	  {(example.problem?.image || example.problem?.images?.[0]) && (
            <div style={{ margin: '12px 0' }}>
              <MathDiagram data={example.problem?.image || example.problem?.images?.[0]} />
            </div>
          )}
        </div>
      )}

      {steps.length > 0 && (
        <div className="steps-section">
          <div className="steps-header">
            <h4>Solution Steps</h4>
            <button className="toggle-steps-btn" onClick={toggleShowAllSteps}>
              {showAllSteps ? 'Show One at a Time' : 'Show All Steps'}
            </button>
          </div>

          {showAllSteps ? (
            <div className="all-steps">
              {steps.map((step, index) => renderStep(step, index))}
            </div>
          ) : (
            <div className="step-navigator">
              <div className="step-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`step-indicator ${currentStep === index ? 'active' : ''} ${currentStep > index ? 'completed' : ''}`}
                    onClick={() => handleStepClick(index)}
                  >
                    {currentStep > index ? <CheckCircle size={16} /> : index + 1}
                  </button>
                ))}
              </div>

              <div className="current-step">
                {renderStep(steps[currentStep], currentStep)}
              </div>

              <div className="step-navigation">
                <button
                  className="nav-btn prev-btn"
                  onClick={handlePrevStep}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>
                <span className="step-counter">
                  Step {currentStep + 1} of {steps.length}
                </span>
                <button
                  className="nav-btn next-btn"
                  onClick={handleNextStep}
                  disabled={currentStep === steps.length - 1}
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {shouldShowFinalAnswer() && (
        <div className="final-answer-section">
          <h4><Calculator size={18} /> Final Answer</h4>
          <div className="final-answer-content">
            {renderMathContent(typeof actualFinalAnswer === "object" && actualFinalAnswer !== null ? (actualFinalAnswer.latex || actualFinalAnswer.value || JSON.stringify(actualFinalAnswer)) : String(actualFinalAnswer))}
          </div>
        </div>
      )}

      {keyConcepts && keyConcepts.length > 0 && (
        <div className="key-concepts-section">
          <h4>Key Concepts</h4>
          <ul>
            {keyConcepts.map((concept, index) => (
              <li key={index}>{renderMathContent(concept)}</li>
            ))}
          </ul>
        </div>
      )}

      {commonMistakes && commonMistakes.length > 0 && (
        <div className="common-mistakes-section">
          <h4>Common Mistakes to Avoid</h4>
          <ul>
            {commonMistakes.map((mistake, index) => {
              const txt = typeof mistake === 'object' && mistake !== null
                ? (mistake.mistake || mistake.text || String(mistake))
                : String(mistake);
              const fix = typeof mistake === 'object' && mistake !== null
                ? mistake.correction : null;
              return (
                <li key={index}>
                  <strong>{String.fromCodePoint(0x274C)} </strong>{renderMathContent(txt)}
                  {fix && (<><br/><strong>{String.fromCodePoint(0x2705)} </strong>{renderMathContent(fix)}</>)}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {practiceProblems && practiceProblems.length > 0 && (
        <div className="practice-problems-section">
          <h4>Practice Problems</h4>
          <ul>
            {practiceProblems.map((problem, index) => (
              <li key={index}>{renderMathContent(problem)}</li>
            ))}
          </ul>
        </div>
      )}
      <ReportContentDialog
        open={reportOpen}
        onClose={handleReportClose}
        contentItem={example}
        contentType="worked-example"
      />
    </div>
  );
};

export default WorkedExampleDisplay;
